import _get from 'lodash/get';

import { getReference } from '@atc/bonnet-reference';
import { brands } from '@atc/brand';

import srpCitiesCrawlPathsDuck from '@/ducks/srp/srpCitiesCrawlPathsDuck.js';
import srpContentDuck from '@/ducks/srp/srpContentDuck';
import srpCrawlPathsDataMapsDuck from '@/ducks/srp/srpCrawlPathsDataMapsDuck.js';
import srpDealershipsCrawlPathsDuck from '@/ducks/srp/srpDealershipsCrawlPathsDuck.js';
import srpListingTypesCrawlPathsDuck from '@/ducks/srp/srpListingTypesCrawlPathsDuck.js';
import srpMakeCrawlPathsDuck from '@/ducks/srp/srpMakeCrawlPathsDuck.js';
import srpModelCrawlPathsDuck from '@/ducks/srp/srpModelCrawlPathsDuck.js';
import srpTrimCrawlPathsDuck from '@/ducks/srp/srpTrimCrawlPathsDuck';
import srpYearCrawlPathsDuck from '@/ducks/srp/srpYearCrawlPathsDuck.js';

import { getCrawlpathDataMaps } from './crawlpath/CrawlpathDataMaps';
import {
    addConditionToAdditionalData, additionalData,
    determineIndexedZip, dispatchAdditionalCrawlPaths,
    getAdditionalCitiesCrawlPathQuery, shouldIncludeLink,
    shouldRenderAllCrawlPaths,
} from './crawlpath/CrawlpathHelpers';
import ddpUrlBuilder from './ddpUrlBuilder';
import getPathSRP from './getPathSRP';

/**
 * Cache for srpHighIndexURLs Set to avoid recreating on every request.
 * Uses a simple hash-based approach to detect if the array content has changed.
 */
let cachedHighIndexURLsSet = null;
let cachedHighIndexURLsHash = null;

/**
 * Creates a simple hash from URL array to detect if content changed
 * @param {Array<string>} urls - Array of URLs
 * @returns {string} Hash representing the array state
 */
const createUrlsHash = (urls) => {
    if (urls.length === 0) {
        return 'empty';
    }
    return `${urls.length}-${urls[0]}-${urls[urls.length - 1]}`;
};

/**
 * Normalizes URLs by extracting the path starting from '/cars-for-sale'
 * @param {Array<string>} urls - Array of URLs to normalize
 * @returns {Set<string>} Set of normalized URL paths
 */
const createNormalizedUrlsSet = (urls) => new Set(
    urls.map((url) => {
        const startIndex = url.indexOf('/cars-for-sale');
        if (startIndex === -1) {
            return url;
        }
        return url.substring(startIndex);
    })
);

/**
 * Gets or creates a cached Set of normalized high index URLs
 * @param {Array<string>} srpHighIndexURLs - Array of high index URLs
 * @returns {Set<string>} Cached or newly created Set of normalized URLs
 */
const getSrpHighIndexUrlsSet = (srpHighIndexURLs) => {
    const currentHash = createUrlsHash(srpHighIndexURLs);

    // Return cached Set if hash matches
    if (cachedHighIndexURLsHash === currentHash) {
        return cachedHighIndexURLsSet;
    }

    // Create new Set and update cache
    const newSet = createNormalizedUrlsSet(srpHighIndexURLs);
    cachedHighIndexURLsSet = newSet;
    cachedHighIndexURLsHash = currentHash;

    return newSet;
};

const getMakeData = () => ({
    path: 'makesCrawlPaths',
    list: ['links'],
});

const getModelData = () => ({
    path: 'modelCrawlPaths',
    list: ['links'],
});

const getYearsData = () => ({
    path: 'yearCrawlPaths',
    list: ['links'],
});

const getCitiesData = () => ({
    path: 'citiesCrawlPaths',
    list: ['links'],
});

const getDealershipsData = () => ({
    path: 'topDealersPaths',
    list: ['links'],
});

const getListingTypesData = () => ({
    path: 'listingTypesCrawlPaths',
    list: ['links'],
});

const getTrimsData = () => ({
    path: 'trimCrawlPaths',
    list: ['links'],
});

// NOTE: Gets the code for make or model from reference data
const getCode = (value, { success, payload }) => {
    if (success) {
        const filteredItem = payload?.filter((item) => item.name === value);

        if (filteredItem?.length === 1) {
            return filteredItem[0].code;
        }
    }

    return null;
};

const buildQuery = async (query, item, storeKey, referenceData, rest) => {
    const { shouldAddAdditionalFilters } = rest;

    // Accepted params: make, model, trim, vehicleStyle, listingType, location
    let crawlPathQuery = {
        experience: query.experience,
        makeCode: query.makeCode,
        modelCode: query.modelCode,
        trimCode: query.trimCode,
        vehicleStyleCode: query.vehicleStyleCode,
        listingType: query.listingType,
        city: query.city,
        state: query.state,
        startYear: query.startYear,
        endYear: query.startYear,
        zip: query.zip,
        dealership: query.dealership,
    };

    const additionalCitiesCrawlPathQuery = getAdditionalCitiesCrawlPathQuery(query, shouldAddAdditionalFilters);

    switch (storeKey) {
        case 'citiesData':
            crawlPathQuery = { ...crawlPathQuery, city: item.city, state: item.state, zip: item.zip, ...additionalCitiesCrawlPathQuery };
            break;
        case 'makesData':
            crawlPathQuery = { ...crawlPathQuery, makeCode: getCode(item.value, referenceData) };
            break;
        case 'modelData':
            crawlPathQuery = { ...crawlPathQuery, modelCode: getCode(item.value, referenceData) };
            break;
        case 'yearsData':
            crawlPathQuery = { ...crawlPathQuery, startYear: item.value, endYear: item.value };
            break;
        case 'trimData':
            crawlPathQuery = { ...crawlPathQuery, trimCode: item.value };
            break;
        case 'listingTypeData':
            crawlPathQuery = { ...crawlPathQuery, listingType: item.value };
            break;
        case 'dealershipsData':
            crawlPathQuery = { ...crawlPathQuery, city: item.city, state: item.state, zip: item.zip };
            break;
        default:
            break;
    }

    return crawlPathQuery;
};

// update a collection of data with  a CS formatted path
// using a supplied query and dynamic parameter value
const updateWithPath = async ({ data = [], query = {}, brand, storeKey, referenceData = {}, ddpBasePath, ...rest }) => {
    const { srpHighIndexURLsSet } = rest;
    // only when brand is ford & makes data
    if (brand === brands.FORD_BRAND && storeKey === 'makesData') {
        delete query.city;
        delete query.state;
        delete query.zip;
    }

    let queryCopy = { ...query };
    const buildPromises = data.map(async (item) => {
        if (item.codes) {
            if (item.codes.startYear && item.codes.endYear) {
                item.codes.startYear = item.codes.startYear[0];
                item.codes.endYear = item.codes.endYear[0];
            }
        }

        queryCopy = await buildQuery(query, item, storeKey, referenceData, rest);

        // zip is unnecessary since it will always be the center zip for crawlpath links
        if (queryCopy.zip) {
            delete queryCopy.zip;
        }

        // remove location data unless we are building cities crawl path links
        if (storeKey !== 'citiesData') {
            delete queryCopy.state;
            delete queryCopy.city;
            delete queryCopy.location;
            delete queryCopy.searchRadius;
            delete queryCopy.dma;
        }

        let path;
        const updatedData = { ...item };

        if (storeKey === 'dealershipsData') {

            Object.assign(item.owner, {
                location: {
                    address: {
                        state: query.state,
                        city: query.city,
                    },
                },
            });
            path = ddpUrlBuilder(item.owner, ddpBasePath);

            // remove the location from each owner in store
            delete item.owner.location;
            updatedData.link = path;
        } else {
            path = await getPathSRP({
                ...queryCopy,
                ...item.codes,
            }, {
                brand,
                basePath: true,
            });

            if (shouldIncludeLink(path, storeKey, srpHighIndexURLsSet)) {
                updatedData.link = path;
            }
        }

        return updatedData;
    });

    return Promise.all(buildPromises);
};

// utility to inspect if there is crawlPath data to parse
// if not then reset the crawlPath state to hide the data
// essentially used to hide the crawl path on subsequent srp renders from filter changes
const processCrawlPathData = async ({
    brand,
    duck,
    dataPath,
    dispatch,
    storeKey,
    lists,
    disableTrim = false,
    pageData,
    ddpBasePath,
    ...rest
}) => {
    const data = _get(pageData, dataPath, false);

    if (data) {
        // update the data with paths built by bonnet-paths
        const updatePromises = lists.map(async (key) => ({
            [key]: await updateWithPath({
                data: data[key], brand, storeKey, ddpBasePath, ...rest,
            }),
        }));
        const updatedLists = await Promise.all(updatePromises);

        // update the data set with the updated lists with paths
        const updatedData = {
            ...data,
            ...updatedLists.reduce((acc, list) => {
                if (Array.isArray(list.links)) {
                    list.links = list.links.filter((item) => item.link); // ensure links only contains items with a link
                }
                return { ...acc, ...list };
            }, {}),
            disableTrim,
        };

        return dispatch(duck.creators.setKey(storeKey, updatedData));
    }

    return dispatch(duck.creators.reset());
};

export default async function processCrawlPath({
    brand = 'atc',
    data = {},
    dispatch = () => { },
    enableIndexedLocations = false,
    isIndexedZip = undefined,
    srpSelfServiceSeo = {},
    srpHighIndexURLs = [],
    query = {},
    ddpBasePath,
    options,
}) {
    const isAtcBranded = brand === brands.ATC_BRAND;

    const queryList = {
        listingType: query.listingType,
        makeCode: query.makeCode,
        modelCode: query.modelCode,
        trimCode: query.trimCode,
        vehicleStyleCode: query.vehicleStyleCode,
        city: query.city,
        state: query.state,
        zip: query.zip,
        startYear: query.startYear,
        endYear: query.endYear,
        fuelTypeGroup: query.fuelTypeGroup,
        experience: query.experience,
    };

    // Get or create cached Set of normalized high index URLs
    const srpHighIndexURLsSet = getSrpHighIndexUrlsSet(srpHighIndexURLs);

    const { isNational, onlyContainsMakeOrMakeModel } = options || {};

    const {
        indexable_locations: indexableLocations = 'high',
    } = srpSelfServiceSeo || {};

    let locationIndexValue = enableIndexedLocations ? indexableLocations : 'all';
    const isHighIndexable = locationIndexValue === 'high' || locationIndexValue === 'highest';

    const { indexedZip, locationIndexValue: updatedLocationIndexValue } = await determineIndexedZip(
        isIndexedZip,
        query,
        brand,
        locationIndexValue,
    );
    locationIndexValue = updatedLocationIndexValue;
    dispatch(srpContentDuck.creators.setKeys({ isIndexedZip: indexedZip }));

    const shouldUseFullCrawlpath = shouldRenderAllCrawlPaths(isAtcBranded, onlyContainsMakeOrMakeModel, indexedZip, isNational, isHighIndexable);

    // For city crawlpath, add additional filters to build crawl path links
    const shouldAddAdditionalFilters = isAtcBranded && isHighIndexable && !onlyContainsMakeOrMakeModel;
    const additionalCitiesCrawlPathQuery = getAdditionalCitiesCrawlPathQuery(query, shouldAddAdditionalFilters);

    // Disable some crawl path modules EXCEPT city list when having 2 filters including Body Style
    // This only applied for ATC and Ford
    const disableNonCitiesCrawlpath = brand !== brands.KBB_BRAND && !!(queryList.vehicleStyleCode
        && (queryList.startYear || queryList.endYear || queryList.makeCode || queryList.modelCode || queryList.trimCode));

    // retrieve make and vehiclestyle data for mapping
    const { makesMap, vehicleStylesMap, modelsMap, subStyleMap, driveGroupMap, engineGroupMap, fuelTypeGroupMap, extColorSimpleMap } = await getCrawlpathDataMaps(queryList.makeCode);
    dispatch(srpCrawlPathsDataMapsDuck.creators.setKey({ makesMap, vehicleStylesMap, modelsMap, subStyleMap, driveGroupMap, engineGroupMap, fuelTypeGroupMap, extColorSimpleMap }));

    const sharedCrawlPathData = {
        brand,
        dispatch,
        pageData: data,
        query: queryList,
    };

    if (shouldUseFullCrawlpath) {
        // process make/model crawlpath
        const makes = getMakeData();
        const makesReferenceData = await getReference('makeCode');
        await processCrawlPathData({
            ...sharedCrawlPathData,
            duck: srpMakeCrawlPathsDuck,
            dataPath: makes.path,
            storeKey: 'makesData',
            lists: makes.list,
            referenceData: makesReferenceData,
            disableTrim: false,
            srpHighIndexURLsSet,
        });

        // process model crawlpath for lsc
        const models = getModelData();
        const makeKnown = !!queryList.makeCode;
        const modelReferenceData = makeKnown ? await getReference('modelCode', { makeCode: queryList.makeCode }) : {};
        await processCrawlPathData({
            ...sharedCrawlPathData,
            duck: srpModelCrawlPathsDuck,
            dataPath: models.path,
            storeKey: 'modelData',
            lists: models.list,
            referenceData: modelReferenceData,
            srpHighIndexURLsSet,
        });

        // process year crawlpath
        const years = getYearsData();
        await processCrawlPathData({
            ...sharedCrawlPathData,
            duck: srpYearCrawlPathsDuck,
            dataPath: years.path,
            storeKey: 'yearsData',
            lists: years.list,
            srpHighIndexURLsSet,
        });

        // process cities crawlpath
        const cities = getCitiesData();
        await processCrawlPathData({
            ...sharedCrawlPathData,
            query: { ...sharedCrawlPathData.query, ...additionalCitiesCrawlPathQuery },
            duck: srpCitiesCrawlPathsDuck,
            dataPath: cities.path,
            storeKey: 'citiesData',
            lists: cities.list,
            shouldAddAdditionalFilters,
            srpHighIndexURLsSet,
        });

        // process listingType crawlpath
        const listingTypes = getListingTypesData();
        await processCrawlPathData({
            ...sharedCrawlPathData,
            duck: srpListingTypesCrawlPathsDuck,
            dataPath: listingTypes.path,
            storeKey: 'listingTypeData',
            lists: listingTypes.list,
            srpHighIndexURLsSet,
        });

        // process trims crawlpath
        const trims = getTrimsData();
        await processCrawlPathData({
            ...sharedCrawlPathData,
            duck: srpTrimCrawlPathsDuck,
            dataPath: trims.path,
            storeKey: 'trimData',
            query: {
                listingType: query.listingType,
                makeCode: query.makeCode,
                modelCode: query.modelCode,
                city: query.city,
                state: query.state,
                zip: query.zip,
                startYear: query.startYear,
                endYear: query.endYear,
            },
            lists: trims.list,
            srpHighIndexURLsSet,
        });

        // process additional crawlpath
        const additionalCrawlPathData = additionalData(brand);
        const updatedAdditionalCrawlPathData = addConditionToAdditionalData(brand, additionalCrawlPathData, query);

        // dispatch additional crawlpath data
        dispatchAdditionalCrawlPaths(brand, query, disableNonCitiesCrawlpath, dispatch, updatedAdditionalCrawlPathData);

    } else {
        // process model crawlpath for non-canonical and new vehicle pages
        if (queryList.listingType === 'NEW') {
            const models = getModelData();
            const makeKnown = !!queryList.makeCode;
            const modelReferenceData = makeKnown ? await getReference('modelCode', { makeCode: queryList.makeCode }) : {};
            await processCrawlPathData({
                ...sharedCrawlPathData,
                duck: srpModelCrawlPathsDuck,
                dataPath: models.path,
                storeKey: 'modelData',
                lists: models.list,
                referenceData: modelReferenceData,
                srpHighIndexURLsSet,
            });
        }
        // process cities crawlpath for non-canonical pages
        const cities = getCitiesData();
        await processCrawlPathData({
            ...sharedCrawlPathData,
            query: { ...sharedCrawlPathData.query, ...additionalCitiesCrawlPathQuery },
            duck: srpCitiesCrawlPathsDuck,
            dataPath: cities.path,
            storeKey: 'citiesData',
            lists: cities.list,
            shouldAddAdditionalFilters,
            srpHighIndexURLsSet,
        });

    }

    if (locationIndexValue !== 'low') {
        // process dealerships crawlpath
        const dealerships = getDealershipsData();
        await processCrawlPathData({
            ...sharedCrawlPathData,
            duck: srpDealershipsCrawlPathsDuck,
            dataPath: dealerships.path,
            storeKey: 'dealershipsData',
            lists: dealerships.list,
            query: {
                city: query.city,
                state: query.state,
            },
            ddpBasePath,
            srpHighIndexURLsSet,
        });
    }
}

/**
 * Clears the srpHighIndexURLs cache
 * Useful for testing and when cache needs to be reset
 */
export const clearSrpHighIndexUrlsCache = () => {
    cachedHighIndexURLsSet = null;
    cachedHighIndexURLsHash = null;
};

// Export cache utilities for testing
export {
    createUrlsHash,
    createNormalizedUrlsSet,
    getSrpHighIndexUrlsSet,
};
